<template>
	<section class="profile-card">
		<div class="profile-main">
			<div class="profile-initials">{{ initials }}</div>
			<div class="profile-copy">
				<h1>{{ name }}</h1>
				<p class="profile-role">{{ role }}</p>
				<p v-if="location" class="profile-location">{{ location }}</p>
				<div class="profile-sources">
					<span
						v-for="progress in providerProgress"
						:key="progress.provider.id"
						class="profile-source"
						:class="[progress.provider.id, progress.status]"
					>
						{{ progress.provider.label
						}}<template v-if="progress.status !== ContactResearchProviderStatus.Success">
							— {{ statusLabel(progress).toLocaleLowerCase() }}</template
						>
					</span>
				</div>
			</div>
		</div>
		<div class="profile-actions">
			<Button class="profile-link-button" variant="secondary" size="l" disabled>
				<AppIcon :name="IconName.Hubspot" :size="16" />
				{{ t("contactResearchPage.result.pushToHubSpot") }}
			</Button>
			<Button
				v-if="linkedInUrl"
				class="profile-link-button"
				variant="secondary"
				size="l"
				:href="linkedInUrl"
				target="_blank"
				rel="noreferrer"
			>
				<AppIcon :name="IconName.LinkedIn" :size="16" />
				{{ t("contactResearchPage.result.openLinkedIn") }}
			</Button>
		</div>
	</section>
</template>

<script setup lang="ts">
import { useI18n } from "vue-i18n"
import AppIcon from "@/components/AppIcon.vue"
import Button from "@/components/ui/Button.vue"
import { IconName } from "@/components/icons/registry"
import { ContactResearchProviderStatus, type ContactResearchProviderProgress } from "@/types/contactResearch"

defineProps<{
	initials: string
	name: string
	role: string
	location?: string
	linkedInUrl?: string
	providerProgress: ContactResearchProviderProgress[]
}>()

const { t } = useI18n()

function statusLabel(progress: ContactResearchProviderProgress): string {
	return t(`contactResearchPage.status.${progress.status}`)
}
</script>

<style scoped>
.profile-card {
	display: flex;
	align-items: flex-start;
	justify-content: space-between;
	gap: var(--space-24);
	min-width: 0;
	padding: var(--space-24);
	background: var(--neutral-0);
	border: var(--border-width-thin) solid var(--neutral-200);
	border-radius: var(--border-radius-md);
	box-shadow: var(--shadow-sm);
}

.profile-main {
	display: flex;
	align-items: flex-start;
	gap: var(--space-18);
	min-width: 0;
}

.profile-initials {
	display: grid;
	place-items: center;
	flex: 0 0 var(--space-64);
	height: var(--space-64);
	color: var(--primary-green);
	font-size: var(--font-size-xl);
	font-weight: var(--font-weight-semibold);
	background: color-mix(in srgb, var(--primary-green) 7%, white);
	border: var(--border-width-thin) solid color-mix(in srgb, var(--primary-green) 35%, white);
	border-radius: var(--border-radius-md);
}

.profile-copy {
	min-width: 0;

	h1,
	p {
		margin: 0;
	}

	h1 {
		font-size: var(--font-size-2xl);
	}
}

.profile-role {
	margin-top: var(--space-6) !important;
	font-size: var(--font-size-base);
}

.profile-location {
	margin-top: var(--space-4) !important;
	color: var(--neutral-600);
	font-size: var(--font-size-small);
}

.profile-sources {
	display: flex;
	flex-wrap: wrap;
	gap: var(--space-6);
	margin-top: var(--space-12);
}

.profile-source {
	display: inline-flex;
	align-items: center;
	min-height: var(--space-24);
	padding: var(--space-3) var(--space-8);
	color: var(--neutral-600);
	font-size: var(--font-size-xs);
	font-weight: var(--font-weight-medium);
	background: var(--neutral-100);
	border: var(--border-width-thin) solid var(--neutral-200);
	border-radius: var(--border-radius-full);

	&.surfe {
		color: var(--primary-green);
		background: color-mix(in srgb, var(--primary-green) 7%, var(--neutral-0));
		border-color: color-mix(in srgb, var(--primary-green) 35%, var(--neutral-0));
	}

	&.webSearch {
		color: var(--accent-info);
		background: var(--accent-info-background);
		border-color: color-mix(in srgb, var(--accent-info) 35%, var(--neutral-0));
	}

	&.hunter {
		color: var(--accent-warning);
		background: color-mix(in srgb, var(--accent-warning) 7%, var(--neutral-0));
		border-color: color-mix(in srgb, var(--accent-warning) 25%, var(--neutral-0));
	}

	&.canada411 {
		color: var(--accent-dev-potential-medium);
		background: color-mix(in srgb, var(--accent-dev-potential-medium) 14%, var(--neutral-0));
		border-color: currentColor;
	}

	&.empty,
	&.skipped,
	&.error {
		color: var(--neutral-600);
		background: var(--neutral-100);
		border-color: var(--neutral-200);
	}
}

.profile-actions {
	display: grid;
	flex: 0 0 220px;
	gap: var(--space-8);
}

.profile-link-button {
	gap: var(--space-8);
	width: 100%;
	height: var(--space-40);
	min-width: 0;
}

@media (max-width: 640px) {
	.profile-card {
		flex-direction: column;
	}

	.profile-actions {
		width: 100%;
		flex-basis: auto;
	}
}
</style>
